import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';

// Dialog for the goodwill cancellation ("Storno mit Kulanz-Gutschein"): an already-paid
// booking that cannot attend is canceled — the resource is released — but instead of a
// refund the customer receives a goodwill voucher over the paid amount minus the
// ticket-insurance share (like the other goodwill vouchers). Leadership-only action.
function GoodwillCancellationDialog({ open, onClose, onConfirm, onPreview, submitting = false }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const previewSeqRef = useRef(0);

  useEffect(() => {
    if (!open) {
      setPreview(null);
      setLoadError(false);
      return;
    }
    const seq = ++previewSeqRef.current;
    setLoading(true);
    setLoadError(false);
    Promise.resolve(onPreview())
      .then(res => {
        if (seq !== previewSeqRef.current) return;
        setPreview(res);
      })
      .catch(() => {
        if (seq !== previewSeqRef.current) return;
        setLoadError(true);
      })
      .finally(() => {
        if (seq === previewSeqRef.current) setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const eligible = preview?.eligible === true;
  const canConfirm = !submitting && !loading && eligible;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='sm' fullWidth>
      <DialogTitle>Storno mit Kulanz-Gutschein</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Typography variant='body2'>
            Die Buchung wird storniert und die Ressource freigegeben — der bereits bezahlte Betrag
            wird <b>nicht erstattet</b>. Stattdessen erhält der Kunde einen <b>Kulanz-Gutschein</b>{' '}
            über den bezahlten Betrag <b>abzüglich der Ticketversicherung</b> (wie bei den anderen
            Kulanz-Gutscheinen).
          </Typography>
          <Alert severity='info'>
            <Stack spacing={0.5}>
              <span>
                • Es wird <b>kein Geld zurückgezahlt</b> — weder online noch bar.
              </span>
              <span>
                • Der Gutschein wird dem Kunden per <b>E-Mail</b> zugeschickt (zusätzlich zur
                Storno-Bestätigung).
              </span>
              <span>
                • Mit Ticketversicherung bitte die <b>reguläre Stornierung mit Erstattung</b> nutzen.
              </span>
            </Stack>
          </Alert>

          {loading ? (
            <Stack spacing={1}>
              <Skeleton variant='text' width='66%' />
              <Skeleton variant='text' width='50%' />
              <Skeleton variant='text' width='60%' />
            </Stack>
          ) : loadError ? (
            <Alert severity='error'>Fehler beim Berechnen des Gutschein-Werts.</Alert>
          ) : preview && !eligible ? (
            <Alert severity='error'>
              {preview.message || 'Kulanz-Storno ist für diese Buchung nicht möglich.'}
            </Alert>
          ) : preview ? (
            <Stack spacing={0.5}>
              <Typography variant='body2'>
                Bezahlter Betrag: <b>{Number(preview.paidAmount || 0).toFixed(2)} €</b>
              </Typography>
              {preview.insuranceDeducted && (
                <Typography variant='body2'>
                  Abzug Ticketversicherung: <b>−{Number(preview.insuranceDeduction || 0).toFixed(2)} €</b>
                </Typography>
              )}
              <Typography variant='body2'>
                Gutschein-Wert: <b>{Number(preview.voucherValue || 0).toFixed(2)} €</b>
              </Typography>
            </Stack>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Abbrechen
        </Button>
        <Button color='error' variant='contained' onClick={onConfirm} disabled={!canConfirm}>
          {submitting ? <CircularProgress size={20} /> : 'Stornieren & Gutschein ausstellen'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default GoodwillCancellationDialog;
